import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  FiMapPin,
  FiClock,
  FiCalendar,
  FiMonitor,
  FiBriefcase,
  FiUsers,
  FiDollarSign,
} from "react-icons/fi";
import TraineeHeader from "../Trainee/TraineeHeader";
import SaveButton, { SAVED_TYPES } from "../Trainee/SaveButton";
import InternshipHeader from "../Home/InternshipHeader";
import Footers from "../Home/Footers";
import AxiosInstance from "../Home/AxiosInstance";
import { storeTokens, buildTraineeSignInPayload } from "../Home/auth";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Home/publicPages.css";
import "./InternshipDetails.css";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
];

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

const getLogoUrl = (logo) => {
  if (!logo) return null;
  const path = String(logo).replace(/\\/g, "/");
  if (path.startsWith("http")) return path;
  return `${AxiosInstance.defaults.baseURL}/media/${path}`;
};

export default function InternshipDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [internship, setInternship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isFromTraineeProfile =
    location.state?.from === "trainee-profile" ||
    Boolean(localStorage.getItem("traineeId"));
  const [traineeId, setTraineeId] = useState(() => {
    const storedId = localStorage.getItem("traineeId");
    return storedId ? parseInt(storedId) : null;
  });

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    mobile_no: "",
    name: "",
    resume: null,
  });

  useEffect(() => {
    AxiosInstance.get(`companies/internship/${id}/`)
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          setInternship(data[0]);
        } else {
          alert("Internship not found.");
        }
      })
      .catch(() => alert("Failed to fetch internship details."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (traineeId) {
      AxiosInstance.get(`companies/application/${id}/`)
        .then((res) => {
          const applications = res.data;
          const applied = applications.some(
            (app) => app.trainee?.trainee_id === traineeId
          );
          setHasApplied(applied);
        })
        .catch((err) => {
          console.error("Failed to fetch applications:", err);
        });
    } else {
      setHasApplied(false);
    }
  }, [id, traineeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, resume: e.target.files[0] }));
  };

  const resetModal = () => {
    setShowModal(false);
    setIsSigningIn(true);
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      first_name: "",
      last_name: "",
      mobile_no: "",
      name: "",
      resume: null,
    });
    setErrorMessage("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const { email, password, confirmPassword, name, resume } = formData;

    if (!traineeId) {
      if (isSigningIn) {
        if (!email || !password) {
          setErrorMessage("Please enter email or mobile number and password.");
          return;
        }
        try {
          const res = await AxiosInstance.post(
            "companies/signin",
            buildTraineeSignInPayload(email, password)
          );

          const idReceived = res.data.trainee?.trainee_id;
          if (!idReceived) {
            setErrorMessage("Sign-in failed: trainee_id not received.");
            return;
          }

          const parsedId = parseInt(idReceived);
          setTraineeId(parsedId);
          storeTokens(res.data);
          localStorage.setItem("traineeId", parsedId);
          setErrorMessage("");
          try {
            const appRes = await AxiosInstance.get(
              `companies/application/${id}/`
            );
            const applications = appRes.data;
            const alreadyApplied = applications.some(
              (app) => app.trainee?.trainee_id === parsedId
            );

            if (alreadyApplied) {
              navigate("/trainee-profile");
              return;
            }
          } catch (err) {
            console.error(
              "Error checking application status after sign-in",
              err
            );
          }
          setFormData((prev) => ({
            ...prev,
            password: "",
            confirmPassword: "",
          }));
        } catch (err) {
          console.error(err);
          setErrorMessage(err.response?.data?.error || "Sign-in failed.");
          return;
        }
      } else {
        if (!email || !password || !confirmPassword) {
          setErrorMessage("Please fill all fields.");
          return;
        }
        if (password !== confirmPassword) {
          setErrorMessage("Passwords do not match.");
          return;
        }
        if (formData.password.length < 6) {
          setErrorMessage("Password must be at least 6 characters long");
          return;
        }

        try {
          const res = await AxiosInstance.post("companies/signup", {
            email,
            password,
            confirm_password: confirmPassword,
            first_name: formData.first_name,
            last_name: formData.last_name,
            mobile_no: formData.mobile_no,
          });

          const idReceived = res.data.trainee_id;
          if (!idReceived) {
            alert("Signup failed: trainee_id not received.");
            return;
          }

          const parsedId = parseInt(idReceived);
          setTraineeId(parsedId);
          storeTokens(res.data);
          localStorage.setItem("traineeId", parsedId);
          setFormData((prev) => ({
            ...prev,
            password: "",
            confirmPassword: "",
            first_name: "",
            last_name: "",
            mobile_no: "",
          }));
          setErrorMessage("");
        } catch (err) {
          console.error(err);
          const errorData = err.response?.data?.error;
          let message = "Signup failed.";

          if (typeof errorData === "string") {
            message = errorData;
          } else if (typeof errorData === "object") {
            message = Object.entries(errorData)
              .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
              .join("\n");
          }
          setErrorMessage(message);
        }
      }
    } else {
      if (!traineeId || !resume) {
        setErrorMessage("Please enter your details and upload your resume.");
        return;
      }

      const [firstName, ...lastParts] = (name || "").trim().split(" ");
      const lastName = lastParts.join(" ") || "-";

      const data = new FormData();
      data.append("trainee", traineeId);
      data.append("internship", id);
      data.append(
        "first_name",
        firstName || formData.first_name || "Applicant"
      );
      data.append("last_name", lastName === "-" ? formData.last_name || "-" : lastName);
      data.append("resume_path", resume);

      try {
        await AxiosInstance.post("companies/application/", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        alert("Application submitted successfully!");
        setShowModal(false);
        setFormData({
          email: "",
          password: "",
          confirmPassword: "",
          first_name: "",
          last_name: "",
          mobile_no: "",
          name: "",
          resume: null,
        });
        setHasApplied(true);
        setErrorMessage("");
        navigate("/trainee-profile");
      } catch (err) {
        console.error(err);
        const message =
          err.response?.data?.error ||
          (typeof err.response?.data === "object"
            ? JSON.stringify(err.response.data)
            : err.response?.data || "Application submission failed.");
        setErrorMessage(message);
      }
    }
  };

  function formatContent(text) {
    if (!text) return "";

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const output = [];
    let listBuffer = [];

    const flushList = () => {
      if (listBuffer.length > 0) {
        output.push(
          `<ul>${listBuffer.map((item) => `<li>${item}</li>`).join("")}</ul>`
        );
        listBuffer = [];
      }
    };

    lines.forEach((line) => {
      if (/^[-*]\s+/.test(line)) {
        listBuffer.push(line.replace(/^[-*]\s+/, "").trim());
      } else {
        flushList();
        output.push(`<p>${line}</p>`);
      }
    });
    flushList();
    return output.join("");
  }

  if (loading) {
    return (
      <div
        className={`InternshipPage min-vh-100 d-flex flex-column${
          isFromTraineeProfile ? " InternshipPage--trainee" : ""
        }`}
      >
        {isFromTraineeProfile ? <TraineeHeader /> : <InternshipHeader />}
        <p className="id-loading">Loading internship details…</p>
        <Footers />
      </div>
    );
  }

  if (!internship) return null;

  const company = internship.company || {};
  const companyName = company.company_name || "Company";
  const logoUrl = getLogoUrl(company.company_logo);
  const accent = getInitialColor(companyName);
  const skills = (internship.skills || "")
    .split(/[,|]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const applyButton = hasApplied ? (
    <button type="button" className="id-apply-btn applied" disabled>
      Applied
    </button>
  ) : (
    <button
      type="button"
      className="id-apply-btn"
      onClick={() => setShowModal(true)}
    >
      Apply now
    </button>
  );

  return (
    <div
      className={`InternshipPage min-vh-100 d-flex flex-column${
        isFromTraineeProfile ? " InternshipPage--trainee" : ""
      }`}
    >
      {isFromTraineeProfile ? <TraineeHeader /> : <InternshipHeader />}

      <section className="id-hero">
        <div className="id-hero-inner">
          <div className="id-hero-copy">
            <nav className="als-breadcrumb" aria-label="Breadcrumb">
              <Link
                to="/internships"
                state={
                  isFromTraineeProfile ? { from: "trainee-profile" } : undefined
                }
              >
                Internships
              </Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <span aria-current="page">{internship.role}</span>
            </nav>
            <div className="id-badges">
              <span className="id-badge type">Internship</span>
              {internship.work_mode && (
                <span className="id-badge mode">{internship.work_mode}</span>
              )}
              {internship.internship_type && (
                <span className="id-badge mode">
                  {internship.internship_type}
                </span>
              )}
            </div>
            <h1>{internship.role}</h1>
            <p className="id-hero-company">{companyName}</p>
            <div className="id-meta">
              {internship.location && (
                <div className="id-meta-item">
                  <FiMapPin /> {internship.location}
                </div>
              )}
              {internship.duration && (
                <div className="id-meta-item">
                  <FiClock /> {internship.duration}
                </div>
              )}
              {internship.stipend && (
                <div className="id-meta-item">
                  <FiDollarSign /> {internship.stipend}
                </div>
              )}
              {internship.department && (
                <div className="id-meta-item">
                  <FiBriefcase /> {internship.department}
                </div>
              )}
              {internship.education && (
                <div className="id-meta-item">
                  <FiUsers /> {internship.education}
                </div>
              )}
              {internship.apply_by && (
                <div className="id-meta-item">
                  <FiCalendar /> Apply by {internship.apply_by}
                </div>
              )}
            </div>
          </div>
          <div className="id-hero-media">
            <div
              className="id-hero-logo"
              style={
                !logoUrl
                  ? { background: `${accent}1a`, color: accent }
                  : undefined
              }
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                companyName.charAt(0).toUpperCase()
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="id-body flex-grow-1">
        <div className="id-layout">
          <main className="id-main">
            <section className="id-card">
              <h2>About the role</h2>
              <div
                className="id-prose"
                dangerouslySetInnerHTML={{
                  __html:
                    formatContent(internship.job_description) ||
                    "<p>No job description available.</p>",
                }}
              />
            </section>

            {skills.length > 0 && (
              <section className="id-card">
                <h2>Skills required</h2>
                <div className="id-tags">
                  {skills.map((skill) => (
                    <span key={skill}>{skill}</span>
                  ))}
                </div>
              </section>
            )}

            {(internship.vacancies != null && internship.vacancies !== "") && (
              <section className="id-card">
                <h2>Openings</h2>
                <p className="id-vacancies">
                  <FiUsers /> {internship.vacancies} vacanc
                  {Number(internship.vacancies) === 1 ? "y" : "ies"}
                </p>
              </section>
            )}

            <section className="id-card">
              <h2>Timeline</h2>
              <ul className="id-timeline">
                {internship.start_date && (
                  <li>
                    <strong>Start date</strong>
                    <span>{internship.start_date}</span>
                  </li>
                )}
                {internship.end_date && (
                  <li>
                    <strong>End date</strong>
                    <span>{internship.end_date}</span>
                  </li>
                )}
                {internship.apply_by && (
                  <li>
                    <strong>Apply by</strong>
                    <span>{internship.apply_by}</span>
                  </li>
                )}
                {internship.duration && (
                  <li>
                    <strong>Duration</strong>
                    <span>{internship.duration}</span>
                  </li>
                )}
                {internship.department && (
                  <li>
                    <strong>Department</strong>
                    <span>{internship.department}</span>
                  </li>
                )}
                {internship.education && (
                  <li>
                    <strong>Education</strong>
                    <span>{internship.education}</span>
                  </li>
                )}
              </ul>
            </section>
          </main>

          <aside className="id-sidebar">
            <div className="id-apply-card">
              <h3>Internship highlights</h3>
              <ul className="id-apply-facts">
                {internship.stipend && (
                  <li>
                    <FiDollarSign /> Stipend {internship.stipend}
                  </li>
                )}
                {internship.location && (
                  <li>
                    <FiMapPin /> {internship.location}
                  </li>
                )}
                {internship.duration && (
                  <li>
                    <FiClock /> {internship.duration}
                  </li>
                )}
                {internship.work_mode && (
                  <li>
                    <FiMonitor /> {internship.work_mode}
                  </li>
                )}
                {internship.internship_type && (
                  <li>
                    <FiBriefcase /> {internship.internship_type}
                  </li>
                )}
                {internship.department && (
                  <li>
                    <FiBriefcase /> Department {internship.department}
                  </li>
                )}
                {internship.education && (
                  <li>
                    <FiUsers /> Education {internship.education}
                  </li>
                )}
                {internship.apply_by && (
                  <li>
                    <FiCalendar /> Apply by {internship.apply_by}
                  </li>
                )}
              </ul>
              {applyButton}
              <SaveButton itemType={SAVED_TYPES.INTERNSHIP} itemId={id} />
            </div>

            <div className="id-company-card">
              <h3>About the company</h3>
              <div className="id-company-row">
                <div
                  className="id-company-logo"
                  style={
                    !logoUrl
                      ? { background: `${accent}1a`, color: accent }
                      : undefined
                  }
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    companyName.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <strong>{companyName}</strong>
                  {company.email && <span>{company.email}</span>}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footers />

      {showModal && !hasApplied && (
        <div className="id-overlay" onClick={resetModal}>
          <div
            className="id-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="id-modal-head">
              <h5>
                {!traineeId && !isFromTraineeProfile
                  ? isSigningIn
                    ? "Sign in to apply"
                    : "Register to apply"
                  : "Complete your application"}
              </h5>
              <button type="button" className="id-modal-close" onClick={resetModal}>
                ×
              </button>
            </div>
            <div className="id-modal-body">
              {errorMessage && (
                <div className="alert alert-danger" role="alert">
                  {errorMessage}
                </div>
              )}
              <form onSubmit={handleFormSubmit}>
                {!traineeId ? (
                  isSigningIn ? (
                    <>
                      <div className="mb-3">
                        <label>Email or mobile number</label>
                        <input
                          type="text"
                          name="email"
                          className="form-control"
                          placeholder="Enter email or mobile number"
                          value={formData.email}
                          onChange={handleInputChange}
                          autoComplete="username"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label>Password</label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary w-100 mb-2"
                      >
                        Sign In
                      </button>
                      <div>
                        <small>
                          Don&apos;t have an account?{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => setIsSigningIn(false)}
                          >
                            Register
                          </button>
                        </small>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="row mb-3">
                        <div className="col">
                          <label>First Name</label>
                          <input
                            type="text"
                            name="first_name"
                            className="form-control"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col">
                          <label>Last Name</label>
                          <input
                            type="text"
                            name="last_name"
                            className="form-control"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col">
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="col">
                          <label>Mobile Number</label>
                          <input
                            type="tel"
                            name="mobile_no"
                            className="form-control"
                            value={formData.mobile_no}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label>Password</label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label>Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          className="form-control"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary w-100 mb-2"
                      >
                        Register
                      </button>
                      <div>
                        <small>
                          Already have an account?{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => setIsSigningIn(true)}
                          >
                            Sign In
                          </button>
                        </small>
                      </div>
                    </>
                  )
                ) : (
                  <>
                    <div className="mb-3">
                      <label>Full name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="As on your resume"
                      />
                    </div>
                    <div className="mb-3">
                      <label>Resume (PDF/DOC/DOCX)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-primary w-100">
                      Submit Application
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
